import { useCallback, useEffect, useRef, useState } from 'react';

import { useGeolocation } from 'state/geolocation';
import { useOrderContext } from 'state/order';
import { useUIContext } from 'state/ui';

import { useFindAvailableDeliveryRestaurant } from './use-find-available-delivery-restaurant';
import { useFindAvailablePickupRestaurant } from './use-find-available-pickup-restaurant';

export const useRestaurantSearch = () => {
  const searchExecutedRef = useRef(false);
  const pendingSearchRef = useRef(false);
  const [isDeliverySearchComplete, setDeliverySearchCompleted] = useState(false);
  const { isDelivery } = useOrderContext();
  const { setIsStoreOrderUnavailableDialogOpen } = useUIContext();
  const { isLoadingPermission, isPermissionGranted } = useGeolocation();
  const {
    findFirstOrderableRestaurantForDelivery,
    availableRestaurant: availableDeliveryRestaurant,
    clearSearchState: clearDeliverySearchState,
    isLoading: isSearchingDeliveryRestaurant,
  } = useFindAvailableDeliveryRestaurant({ setDeliverySearchCompleted });

  const {
    findFirstOrderableRestaurantForPickUp,
    isLoading: isSearchingPickupRestaurant,
    availableRestaurant,
    clearSearchState: clearPickupSearchState,
  } = useFindAvailablePickupRestaurant();

  const clearAllSearchState = useCallback(() => {
    clearPickupSearchState();
    clearDeliverySearchState();
    setDeliverySearchCompleted(false);
    setIsStoreOrderUnavailableDialogOpen(false);
  }, [clearPickupSearchState, clearDeliverySearchState, setIsStoreOrderUnavailableDialogOpen]);

  const executeSearch = useCallback(() => {
    if (searchExecutedRef.current) {
      return;
    }

    if (isDelivery) {
      findFirstOrderableRestaurantForDelivery();
      searchExecutedRef.current = true;
    } else {
      // In some cases, like when the app is hard closed and reopened the permissions are granted but we are still loading the information.
      // as at this point we know that we should execute the search, we need to keep track of it.
      // so we can retry when the permission is granted and the loading is false.
      pendingSearchRef.current = true;
    }
  }, [isDelivery, findFirstOrderableRestaurantForDelivery]);

  useEffect(() => {
    if (searchExecutedRef.current || !pendingSearchRef.current) {
      return;
    }

    if (isDelivery) {
      return;
    }

    if (isPermissionGranted && !isLoadingPermission) {
      findFirstOrderableRestaurantForPickUp();
      searchExecutedRef.current = true;
      pendingSearchRef.current = false;
    }
  }, [isDelivery, isPermissionGranted, isLoadingPermission, findFirstOrderableRestaurantForPickUp]);

  const resetSearchState = useCallback(() => {
    clearAllSearchState?.();
    searchExecutedRef.current = false;
    pendingSearchRef.current = false;
  }, [clearAllSearchState]);

  return {
    findFirstOrderableRestaurantForPickUp,
    findFirstOrderableRestaurantForDelivery,
    isRestaurantSearchLoading: isSearchingDeliveryRestaurant || isSearchingPickupRestaurant,
    availableRestaurantForDelivery: availableDeliveryRestaurant,
    availableRestaurantForPickUp: availableRestaurant,
    isDeliverySearchComplete,
    clearAllSearchState,
    executeSearch,
    resetSearchState,
    isDelivery,
  };
};
